import { Injectable, inject } from '@angular/core';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { BehaviorSubject, catchError, firstValueFrom, of } from 'rxjs';
import { CompanyAccountControllerService } from '@mobiletract/account-service-angular-client';
import { getUserRoles } from '../auth/keycloak-roles.util';
import { isCompanyInvoiceFilled } from '../../shared/utils/company-invoice.utils';

const COMPANY_ROLE = 'COMPANY';

export interface CompanyDataSetupState {
  loading: boolean;
  required: boolean;
}

@Injectable({ providedIn: 'root' })
export class CompanyDataSetupService {
  private readonly keycloak = inject(Keycloak);
  private readonly companyAccount = inject(CompanyAccountControllerService);

  private readonly stateSubject = new BehaviorSubject<CompanyDataSetupState>({
    loading: true,
    required: false
  });

  readonly companyDataRequired$ = this.stateSubject.asObservable();

  async check(): Promise<boolean> {
    await this.waitForKeycloakInitialization();

    const loggedIn = this.keycloak.authenticated ?? false;
    if (!loggedIn) {
      this.stateSubject.next({ loading: false, required: false });
      return false;
    }

    const roles = getUserRoles(this.keycloak);
    if (!roles.includes(COMPANY_ROLE)) {
      this.stateSubject.next({ loading: false, required: false });
      return false;
    }

    try {
      const keycloakProfile: KeycloakProfile = await this.keycloak.loadUserProfile();
      const attributes = (keycloakProfile as KeycloakProfile & { attributes?: Record<string, unknown> }).attributes;
      if (attributes?.['marketingAgreement'] == null) {
        this.stateSubject.next({ loading: false, required: false });
        return false;
      }

      const profile = await firstValueFrom(
        this.companyAccount.getCompanyProfile().pipe(catchError(() => of(null)))
      );
      const required = !isCompanyInvoiceFilled(profile?.companyInvoice);
      this.stateSubject.next({ loading: false, required });
      return required;
    } catch {
      this.stateSubject.next({ loading: false, required: false });
      return false;
    }
  }

  private async waitForKeycloakInitialization(): Promise<void> {
    const timeoutMs = 10_000;
    const pollMs = 50;
    const startedAt = Date.now();

    while (!this.keycloak.didInitialize && Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
  }
}
